/**
 * IconSelectorModal - Modal para seleccionar icono/estado del prospecto.
 * Renderiza iconos agrupados por categoria semantica (pills) usando ICON_GROUPS.
 * Tooltip con descripcion al hover sobre cada icono.
 *
 * Props:
 *   phone         - Telefono del prospecto (null en bulk mode)
 *   contactName   - Nombre para mostrar en header
 *   currentIconId - ID del icono actualmente seleccionado
 *   onClose       - Callback al cerrar el modal
 *   onIconChanged - Callback despues de actualizar el icono exitosamente
 */

import { useState } from 'react'
import { getAvatarData } from '../../utils/avatarUtils'
import { iconsAPI } from '../../services/api'
import RatingIcon, { NOMBRE_MAP } from './RatingIcon'
import { ICON_GROUPS, ICON_DESCRIPTIONS, PILL_COLORS } from '../../utils/iconDescriptions'
import { Tooltip } from './Tooltip'

function IconGroupRow({ group, children }) {
  if (!group.label) {
    return (
      <div className="flex gap-2 justify-start flex-wrap">
        {children}
      </div>
    )
  }
  const colors = PILL_COLORS[group.color] || PILL_COLORS.green
  return (
    <div className={`rounded-2xl border px-3 py-0.5 ${colors.container}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 px-1 ${colors.label}`}>
        {group.label}
      </div>
      <div className="flex gap-2 justify-start flex-wrap">
        {children}
      </div>
    </div>
  )
}

export default function IconSelectorModal({ phone, contactName, currentIconId, onClose, onIconChanged }) {
  const [saving, setSaving] = useState(false)
  const [activeId, setActiveId] = useState(currentIconId ? (NOMBRE_MAP[currentIconId] || currentIconId) : null)

  const handleSelect = async (iconId) => {
    if (saving) return

    if (!phone) {
      if (onIconChanged) onIconChanged(iconId)
      onClose()
      return
    }

    if (iconId === activeId) return

    setSaving(true)
    try {
      await iconsAPI.updateProspectIcon(phone, iconId)
      setActiveId(iconId)
      if (onIconChanged) onIconChanged(iconId)
      onClose()
    } catch (err) {
      console.error('Error actualizando icono:', err)
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <div className="relative bg-white dark:bg-surface-dark-alt rounded-2xl shadow-2xl w-full max-w-[675px] mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-2.5">
          <h3 className="text-lg font-semibold text-white">Seleccionar Estado</h3>
          {phone && contactName ? (
            <div className="flex items-center gap-2 mt-1">
              {(() => { const av = getAvatarData(contactName, phone); return (
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px]" style={{ background: av.gradient }}>{av.initials}</div>
              ) })()}
              <span className="text-white text-sm font-medium">{contactName}</span>
              <span className="text-primary-200 text-xs">{phone}</span>
            </div>
          ) : (
            <p className="text-primary-100 text-sm">{phone || 'Seleccion masiva'}</p>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-2">
          <div className={`space-y-1 ${saving ? 'opacity-50 cursor-wait' : ''}`}>
            {ICON_GROUPS.map(group => (
              <IconGroupRow key={group.key} group={group}>
                {group.icons.map(iconId => {
                  const isSelected = activeId === iconId
                  const desc = ICON_DESCRIPTIONS[iconId] || { title: iconId, body: '' }
                  return (
                    <Tooltip key={iconId} title={desc.title} body={desc.body} delay={300}>
                      <button
                        onClick={() => handleSelect(iconId)}
                        disabled={saving}
                        className={`
                          p-1.5 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95
                          ${isSelected
                            ? 'bg-gradient-to-br from-primary-100 to-primary-200 border-2 border-primary-400 shadow-md dark:from-primary-900/40 dark:to-primary-800/40 dark:border-primary-500'
                            : 'bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 hover:border-primary-300 dark:hover:border-primary-500 hover:shadow-md'
                          }
                        `}
                        aria-label={desc.title}
                      >
                        <RatingIcon iconId={iconId} size="lg" />
                      </button>
                    </Tooltip>
                  )
                })}
              </IconGroupRow>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-2 flex justify-end">
          <button
            onClick={saving ? undefined : onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-xl transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
